import {
  fontBaseStyle,
  panelBaseColorsStyle,
} from '@blocksuite/affine-shared/styles';
import { unsafeCSSVar, unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { stopPropagation } from '@blocksuite/affine-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import { InformationIcon } from '@blocksuite/icons/lit';
import { PropTypes, requiredProperties } from '@blocksuite/std';
import { autoPlacement, offset, shift } from '@floating-ui/dom';
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { take } from 'rxjs';

import { createLitPortal } from '../portal';

@requiredProperties({
  message: PropTypes.string,
  needUpload: PropTypes.boolean,
  action: PropTypes.instanceOf(Function),
})
export class ResourceStatus extends WithDisposable(LitElement) {
  static override styles = css`
    button.status {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      font-size: 18px;
      outline: none;
      border: none;
      cursor: pointer;
      color: ${unsafeCSSVarV2('button/pureWhiteText')};
      background: ${unsafeCSSVarV2('status/error')};
      box-shadow: ${unsafeCSSVarV2('shadow/overlay')};
    }

    ${panelBaseColorsStyle('.popper')}
    ${fontBaseStyle('.popper')}
    .popper {
      display: flex;
      flex-direction: column;
      gap: 4px;
      outline: none;
      padding: 8px;
      border-radius: 8px;
      width: 260px;
      max-width: calc(100vw - 32px);
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      font-size: ${unsafeCSSVar('fontSm')};
    }

    .header {
      font-weight: 500;
    }

    .content {
      font-feature-settings:
        'liga' off,
        'clig' off;
      color: ${unsafeCSSVarV2('text/primary')};
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 4px;
    }

    button.action {
      display: flex;
      align-items: center;
      padding: 2px 12px;
      border-radius: 8px;
      border: none;
      background: none;
      cursor: pointer;
      outline: none;
      color: ${unsafeCSSVarV2('button/primary')};
    }

    button:focus-visible {
      outline: 2px solid ${unsafeCSSVarV2('button/primary')};
      outline-offset: 2px;
    }
  `;

  private _portalAbort: AbortController | null = null;

  private _togglePopover(event: MouseEvent) {
    if (this._portalAbort) {
      this._portalAbort.abort();
      return;
    }
    const abortController = new AbortController();
    this._portalAbort = abortController;
    abortController.signal.addEventListener('abort', () => {
      this._portalAbort = null;
      this.requestUpdate();
    });
    const { message, needUpload } = this;
    // Render outside the canvas transform so notes cannot cover the action.
    const { portal } = createLitPortal({
      abortController,
      closeOnClickAway: true,
      positionStrategy: 'fixed',
      computePosition: {
        referenceElement: this._trigger,
        middleware: [
          offset(8),
          autoPlacement({ allowedPlacements: ['top-start', 'bottom-start'] }),
          shift({ padding: 8 }),
        ],
        autoUpdate: { animationFrame: true },
      },
      template: ({ positionSlot }) => {
        if (event.detail === 0) {
          const subscription = positionSlot.pipe(take(1)).subscribe(() => {
            if (!abortController.signal.aborted) {
              portal.shadowRoot
                ?.querySelector<HTMLButtonElement>('button.action')
                ?.focus();
            }
          });
          abortController.signal.addEventListener(
            'abort',
            () => subscription.unsubscribe(),
            { once: true }
          );
        }
        return html`
          <style>
            ${ResourceStatus.styles.cssText}
          </style>
          <div
            class="popper"
            @click=${stopPropagation}
            @pointerdown=${stopPropagation}
            @keydown=${(e: KeyboardEvent) => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                abortController.abort();
                this._trigger.focus();
              }
            }}
          >
            <div class="header">
              ${needUpload ? 'Upload' : 'Download'} failed
            </div>
            <div class="content">${message}</div>
            <div class="footer">
              <button
                type="button"
                class="action"
                @click=${() => {
                  abortController.abort();
                  this.action();
                }}
              >
                ${needUpload ? 'Retry' : 'Reload'}
              </button>
            </div>
          </div>
        `;
      },
    });
    this.requestUpdate();
  }

  override firstUpdated() {
    this.disposables.addFromEvent(this, 'click', stopPropagation);
    this.disposables.addFromEvent(this, 'keydown', (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        this._portalAbort?.abort();
      }
    });
    this.disposables.addFromEvent(this._trigger, 'click', (e: MouseEvent) => {
      this._togglePopover(e);
    });
    this.disposables.add(() => this._portalAbort?.abort());
  }

  override render() {
    return html`
      <button
        type="button"
        class="status"
        aria-label=${this.needUpload ? 'Upload failed' : 'Download failed'}
        aria-expanded=${this._portalAbort ? 'true' : 'false'}
      >
        ${InformationIcon()}
      </button>
    `;
  }

  @query('button.status')
  private accessor _trigger!: HTMLButtonElement;

  @property({ attribute: false })
  accessor message!: string;

  @property({ attribute: false })
  accessor needUpload!: boolean;

  @property({ attribute: false })
  accessor action!: () => void;
}
